import { useCrudService } from 'framework/crud';
import { usePeopleManagementApi } from 'infrastructure/api';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useTechnologyService } from 'domain/shared/career';
import { useOfficeService, usePositionService } from 'domain/shared/employee';
import { enrichPositionSlots } from 'domain/shared/positionSlot';
import { VACANCY_POSITION_SLOT_STATUS, VACANCY_POSITION_SLOT_PAGE_SIZE } from './constants';
import { VacancyPositionSlot, VacancyPositionSlotListItem, VacancyPositionSlotScope } from './types';

const normalize = (value?: string | number | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase() || null;

const matchValue = (slotValue: string | number | null, scopeValue?: string | number | null) =>
  !scopeValue || normalize(slotValue) === normalize(scopeValue);

const matchTechnology = (positionSlot: VacancyPositionSlotListItem, technology?: string | null) =>
  !technology || normalize(positionSlot.technologyId) === normalize(technology);

const matchOffices = (positionSlotOfficeIds?: number[], scopeOfficeIds?: number[]) => {
  if (!scopeOfficeIds?.length) {
    return true;
  }

  const normalizedPositionSlotOffices = [...(positionSlotOfficeIds || [])].sort((left, right) => left - right);
  const normalizedScopeOffices = [...scopeOfficeIds].sort((left, right) => left - right);

  return (
    normalizedPositionSlotOffices.length === normalizedScopeOffices.length &&
    normalizedPositionSlotOffices.every((office, index) => office === normalizedScopeOffices[index])
  );
};

export const isVacancyPositionSlotMatchingScope = (
  positionSlot: VacancyPositionSlotListItem,
  scope: Partial<VacancyPositionSlotScope>,
) =>
  matchValue(positionSlot.specializationId, scope.specializationId) &&
  matchTechnology(positionSlot, scope.technologyId) &&
  matchValue(positionSlot.competencyLevel, scope.competencyLevel) &&
  matchValue(positionSlot.businessFunctionId, scope.businessFunctionId) &&
  matchValue(positionSlot.workerType, scope.workerType) &&
  matchOffices(positionSlot.officeIds, scope.officeIds);

const isAvailableForVacancy = (positionSlot: VacancyPositionSlotListItem) =>
  [VACANCY_POSITION_SLOT_STATUS.OPEN, VACANCY_POSITION_SLOT_STATUS.FILLED].includes(positionSlot.status) &&
  positionSlot.vacancyId === null &&
  positionSlot.employeeAliases.length < 2;

export const buildVacancyPositionSlotScope = (
  vacancyScope: Partial<VacancyPositionSlotScope>,
  selectedPositionSlot?: VacancyPositionSlotListItem,
): Partial<VacancyPositionSlotScope> => ({
  specializationId: vacancyScope.specializationId || selectedPositionSlot?.specializationId,
  technologyId: vacancyScope.technologyId || selectedPositionSlot?.technologyId,
  competencyLevel: vacancyScope.competencyLevel || selectedPositionSlot?.competencyLevel,
  businessFunctionId: vacancyScope.businessFunctionId || selectedPositionSlot?.businessFunctionId,
  workerType: vacancyScope.workerType || selectedPositionSlot?.workerType,
  officeIds: vacancyScope.officeIds?.length ? vacancyScope.officeIds : selectedPositionSlot?.officeIds,
});

const useVacancyPositionSlotsService = () => {
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { searchAll } = useCrudService<VacancyPositionSlot>({
    listResourceUrl: '/position-slots',
    singleResourceUrl: '/position-slots/:id',
    useService: usePeopleManagementApi,
  });

  const getAvailablePositionSlots = async (): Promise<VacancyPositionSlotListItem[]> => {
    const [positions, technologies, offices, businessFunctions] = await Promise.all([
      findAllPositions(),
      findAllTechnologies(),
      findAllOffices(),
      findAllBusinessFunctions(),
    ]);

    const response = await searchAll({
      filter: {
        status: {
          in: [VACANCY_POSITION_SLOT_STATUS.OPEN, VACANCY_POSITION_SLOT_STATUS.FILLED],
        },
        vacancyId: {
          NULL: true,
        },
      },
      pageSize: VACANCY_POSITION_SLOT_PAGE_SIZE,
    });
    const positionSlots = enrichPositionSlots(response || [], {
      positions,
      technologies,
      offices,
      businessFunctions,
    });

    return positionSlots.filter(isAvailableForVacancy);
  };

  return {
    getAvailablePositionSlots,
  };
};

export default useVacancyPositionSlotsService;
